/**
 * 级联字段工具：SQL 结果列解析层级映射、扁平数据集构树、值路径回显解析。
 * 列约定：levelN_value / levelN_label（N=1..n 连续，支持任意级数）。
 */

const LEVEL_COL_RE = /^level(\d+)_(value|label)$/i

/**
 * 从结果集列名自动解析级联层级映射。
 * @param {string[]} columns 结果集列名
 * @returns {{value:string,label:string}[]} 按级序的层级映射（label 列缺失时回退 value 列）
 */
export function parseLevelsFromColumns(columns) {
  const map = {}
  ;(columns || []).forEach(col => {
    const m = LEVEL_COL_RE.exec(String(col == null ? '' : col).trim())
    if (!m) return
    const n = Number(m[1])
    if (!map[n]) map[n] = {}
    map[n][m[2].toLowerCase()] = col
  })
  return Object.keys(map)
    .map(Number)
    .sort((a, b) => a - b)
    .filter(n => map[n].value)
    .map(n => ({ value: map[n].value, label: map[n].label || map[n].value }))
}

/**
 * 扁平数据集行构建级联选项树（el-cascader options）。
 * @param {object[]} rows 数据集行
 * @param {{value:string,label:string}[]} levels 层级列映射
 * @returns {object[]} 选项树 [{value,label,children?}]
 */
export function buildCascadeTree(rows, levels) {
  const lv = levels || []
  if (!lv.length) return []
  const root = []
  const rootMap = {}
  ;(rows || []).forEach(row => {
    let list = root
    let map = rootMap
    for (let i = 0; i < lv.length; i++) {
      const raw = row == null ? null : row[lv[i].value]
      if (raw == null || raw === '') return
      const val = String(raw)
      let node = map[val]
      if (!node) {
        const rawLabel = row[lv[i].label]
        node = {
          value: val,
          label: rawLabel == null || rawLabel === '' ? val : String(rawLabel),
          children: []
        }
        map[val] = node
        list.push(node)
      }
      if (i === lv.length - 1) {
        // 叶子不带 children，避免级联面板多出空列
        delete node.children
        return
      }
      if (!node._map) node._map = {}
      list = node.children
      map = node._map
    }
  })
  const prune = list => (list || []).forEach(n => {
    delete n._map
    prune(n.children)
  })
  prune(root)
  return root
}

/**
 * 值路径解析为 label 路径；某级未命中时回退原始值。
 * @param {object[]} tree 选项树
 * @param {Array} path 值路径
 * @returns {string[]} label 路径
 */
export function resolveCascadeLabels(tree, path) {
  const out = []
  let list = tree || []
  ;(path || []).forEach(v => {
    const node = (list || []).find(n => String(n.value) === String(v))
    if (!node) {
      out.push(String(v))
      list = []
      return
    }
    out.push(node.label)
    list = node.children || []
  })
  return out
}

/**
 * 在选项树中按「末级值」反查完整值路径（用于把单值还原为 el-cascader 的 v-model 路径数组）。
 * 仅匹配叶子节点；未命中返回 null（调用方据此判定该值不可用/需跳过）。
 * @param {object[]} tree 选项树（buildCascadeTree 产物）
 * @param {string|number} leafValue 末级值
 * @returns {string[]|null} 值路径数组
 */
export function findCascadePath(tree, leafValue) {
  if (leafValue == null || leafValue === '') return null
  const target = String(leafValue)
  const walk = (list, path) => {
    for (const node of (list || [])) {
      const cur = path.concat([node.value])
      const children = node.children
      if (!children || !children.length) {
        if (String(node.value) === target) return cur
        continue
      }
      const hit = walk(children, cur)
      if (hit) return hit
    }
    return null
  }
  return walk(tree, [])
}
